import {
  INITIAL_SURVEY_TO_SHOW,
  EXPIRY_PERIOD,
  SURVEY_COUNT_LIMIT,
  SURVEY_COUNT_LIMIT_SINGAPORE,
  SURVEY_TIMEOUT_TO_SHOW,
  SURVEY_DATA_LOCAL_STORAGE_KEY,
  SURVEY_DATA_LOCAL_STORAGE_KEY_SINGAPORE,
  SURVEY_SESSION_ACTIVE_KEY,
  SURVEY_SHOWN_THIS_SESSION_KEY,
  SURVEY_COUNTRY_SINGAPORE
} from 'enums/surveyRotation'
import { mapGetters, mapActions } from 'vuex'

export default {
  data() {
    return {
      surveyCounter: 0,
      isNewSession: false,
      expiryTimerId: null,
      isInitialized: false,
      surveyScheduled: false,
      visitorCountry: null
    }
  },
  computed: {
    ...mapGetters({
      isBrowserTabActiveAndFocused: 'isBrowserTabActiveAndFocused'
    }),
    isNextSurveyAvailable() {
      return this.surveyCounter <= this.getSurveyCountLimit()
    }
  },
  watch: {
    isBrowserTabActiveAndFocused: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isInitialized) {
          this.$_surveyRotation_handleTabActive()
        } else {
          this.$_surveyRotation_handleTabInactive()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      requestCurrentCountryInfo: 'countries/requestCurrentCountryInfo'
    }),
    async $_surveyRotation_getCountryInfo() {
      try {
        const countryInfo = await this.requestCurrentCountryInfo()
        this.visitorCountry = countryInfo?.Name
      } catch (err) {
        this.$errorHandler(err, this)
      }
    },
    isSingapore() {
      return this.visitorCountry === SURVEY_COUNTRY_SINGAPORE
    },
    getSurveyStorageKey() {
      return this.isSingapore()
        ? SURVEY_DATA_LOCAL_STORAGE_KEY_SINGAPORE
        : SURVEY_DATA_LOCAL_STORAGE_KEY
    },
    getSurveyCountLimit() {
      return this.isSingapore()
        ? SURVEY_COUNT_LIMIT_SINGAPORE
        : SURVEY_COUNT_LIMIT
    },
    $_surveyRotation_initialization() {
      const surveyData = localStorage.getItem(this.getSurveyStorageKey())

      if (!surveyData) {
        this.$_surveyRotation_createInitialSurveyData()
        return
      }

      try {
        const parsed = JSON.parse(surveyData)
        this.surveyCounter = parsed.counter
      } catch (err) {
        this.$errorHandler(err, this)
      }
    },
    $_surveyRotation_createInitialSurveyData() {
      const surveyData = {
        counter: 1,
        startDate: Date.now(),
        expiryDate: Date.now() + EXPIRY_PERIOD
      }

      localStorage.setItem(
        this.getSurveyStorageKey(),
        JSON.stringify(surveyData)
      )

      this.surveyCounter = INITIAL_SURVEY_TO_SHOW
    },
    $_surveyRotation_detectNewSession() {
      const sessionFlag = sessionStorage.getItem(SURVEY_SESSION_ACTIVE_KEY)

      if (!sessionFlag) {
        this.isNewSession = true
        sessionStorage.setItem(SURVEY_SESSION_ACTIVE_KEY, 'true')
      } else {
        this.isNewSession = false
      }
    },
    $_surveyRotation_handleExpiredSession() {
      let surveyData

      try {
        surveyData = JSON.parse(
          localStorage.getItem(this.getSurveyStorageKey())
        )
      } catch (err) {
        this.$errorHandler(err, this)
      }

      if (!surveyData) return

      const currentDate = Date.now()
      const expiryDate = surveyData.expiryDate

      const isSessionExpired = currentDate > expiryDate

      if (!isSessionExpired) {
        this.$_surveyRotation_scheduleExpiryTimer(currentDate, expiryDate)

        return
      }

      this.$_surveyRotation_resetExpiredSessionAndShowNextSurvey(
        surveyData,
        currentDate
      )
    },
    $_surveyRotation_shouldScheduleSurvey() {
      if (!this.isNewSession) {
        return false
      }

      if (!this.isNextSurveyAvailable) {
        return false
      }

      const shownThisSession = sessionStorage.getItem(
        SURVEY_SHOWN_THIS_SESSION_KEY
      )

      if (shownThisSession) {
        return false
      }

      return true
    },
    $_surveyRotation_handleScheduleSurveyDisplay() {
      if (this.surveyScheduled) {
        return
      }

      this.surveyScheduled = true

      setTimeout(() => {
        if (!this.isBrowserTabActiveAndFocused) {
          this.surveyScheduled = false
          return
        }

        if (this.$_surveyRotation_isScheduledSurveyAlreadyShown()) {
          this.surveyScheduled = false
          return
        }

        this.$bus.$emit('show-popup-survey', {
          surveyCounter: this.surveyCounter,
          visitorCountry: this.visitorCountry
        })

        this.$_surveyRotation_markSurveyShownThisSession()
        this.surveyScheduled = false
      }, SURVEY_TIMEOUT_TO_SHOW)
    },
    $_surveyRotation_isScheduledSurveyAlreadyShown() {
      let surveyData

      try {
        surveyData = JSON.parse(
          localStorage.getItem(this.getSurveyStorageKey())
        )
      } catch (err) {
        this.$errorHandler(err, this)
      }

      return surveyData.counter > this.surveyCounter
    },
    $_surveyRotation_markSurveyShownThisSession() {
      sessionStorage.setItem(SURVEY_SHOWN_THIS_SESSION_KEY, 'true')
      this.$_surveyRotation_incrementCounter()
    },
    $_surveyRotation_incrementCounter() {
      const surveyData = JSON.parse(
        localStorage.getItem(this.getSurveyStorageKey())
      )

      if (!surveyData) return

      if (this.isNextSurveyAvailable) {
        surveyData.counter += 1
        localStorage.setItem(
          this.getSurveyStorageKey(),
          JSON.stringify(surveyData)
        )
        this.surveyCounter = surveyData.counter
      }
    },
    $_surveyRotation_scheduleExpiryTimer(currentDate, expiryDate) {
      const expiringTimeRemaining = expiryDate - currentDate

      this.expiryTimerId = setTimeout(() => {
        this.$_surveyRotation_handleExpiredSession()
      }, expiringTimeRemaining)
    },
    $_surveyRotation_resetExpiredSessionAndShowNextSurvey(
      surveyData,
      currentDate
    ) {
      if (this.isNextSurveyAvailable) {
        surveyData.startDate = currentDate
        surveyData.expiryDate = currentDate + EXPIRY_PERIOD

        localStorage.setItem(
          this.getSurveyStorageKey(),
          JSON.stringify(surveyData)
        )

        this.surveyCounter = surveyData.counter

        this.$_surveyRotation_handleScheduleSurveyDisplay()
      }
    },
    $_surveyRotation_handleTabActive() {
      if (!process.client) return

      this.$_surveyRotation_handleExpiredSession()
    },
    $_surveyRotation_handleTabInactive() {
      if (this.expiryTimerId) {
        clearTimeout(this.expiryTimerId)
        this.expiryTimerId = null
      }
    }
  },
  async mounted() {
    await this.$_surveyRotation_getCountryInfo()
    this.$_surveyRotation_initialization()
    this.$_surveyRotation_detectNewSession()
    this.isInitialized = true

    if (this.isBrowserTabActiveAndFocused) {
      this.$_surveyRotation_handleTabActive()
    }

    if (this.$_surveyRotation_shouldScheduleSurvey()) {
      this.$_surveyRotation_handleScheduleSurveyDisplay()
    }
  }
}
